import AnimationController from '../../assets/js/controllers/animation';
import ResizeController from '../../assets/js/controllers/resize';

import Platform from '../../assets/js/constants/platform';

import { getElementIndex, multiAddEventListener, multiClassList, validateParamBoolean, validateParamNumber } from '../../assets/js/helpers';

export default class Accordion {

	static selector = '[data-accordion]';

	// class variables

	el = {};

	config = {
		default: -1,
		disableDesktop: false
	};

	controllers = {
		resize: null
	};

	states = {
		index: -1,
		total: -1,
		platform: ''
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			triggers: container.querySelectorAll('[data-accordion-trigger]'),
			targets: container.querySelectorAll('[data-accordion-target]'),
		};

		// set states

		this.states.total = this.el.targets.length;

		// set params

		this.config.defaultIndex = validateParamNumber(this.el.container, 'data-accordion-default', -1);
		this.config.disableDesktop = validateParamBoolean(this.el.container, 'data-accordion-desktop-disabled', false, true);

		this.initialize();
	}

	// init

	initialize() {

		this.#addEventListeners();

		if (this.states.platform === Platform.DESKTOP && !this.config.disableDesktop) {
			// set default height to 0 unless active
			for (let i = 0; i < this.el.targets.length; i++) {
				const $target = this.el.targets[i];
				if (!$target.classList.contains('is-active')) {
					$target.style.height = 0;
				}
			}
		}

		if (this.config.defaultIndex >= 0) {
			this.changeItemByIndex(0);
		}
	}

	// event listeners

	#addEventListeners = () => {
		multiAddEventListener(this.el.triggers, 'click', this.#onTriggerClick);

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});
	}

	#removeEventListeners = () => {
		ResizeController.clear(this.controllers.resize);
	}

	// listener methods

	#onTriggerClick = (e) => {
		e.preventDefault();
		e.stopImmediatePropagation();
		const $target = e.currentTarget;
		if ($target.classList.contains('is-active')) {
			this.collapse();
		} else {
			this.changeItem(e.currentTarget);
		}
	}

	// controller methods

	#onResizeUpdate = () => {
		this.states.platform = ResizeController.platform;
		return true;
	}

	#onResizeRender = () => {
		if (this.states.index >= 0) {
			const $target = this.el.targets[this.states.index];
			const $targetContent = $target.children[0];
			this.setExpandHeight($target, $targetContent.offsetHeight);
		}
	}

	// change index

	changeItemByIndex(index) {
		if (index !== this.states.index && index < this.states.total) {
			const $target = this.el.targets[index];
			const $targetContent = $target.children[0];
			const id = $target.getAttribute('data-accordion-target');
			const triggers = this.getTriggersById(id);

			this.collapse();

			$target.classList.add('is-active');
			this.setExpandHeight($target, $targetContent.offsetHeight);
			multiClassList(triggers, 'add', 'is-active');

			AnimationController.one({
				update: () => true,
				render: () => {
					$target.classList.add('is-in');
					multiClassList(triggers, 'add', 'is-in');
				}
			});

			this.states.index = index;
		}
	}

	changeItem($trigger) {
		const $target = this.getTargetById($trigger.getAttribute('data-accordion-trigger'));
		this.changeItemByIndex(getElementIndex(this.el.targets, $target));
	}

	getTargetById(id = '') {
		for (let i = 0; i < this.el.targets.length; i++) {
			const $target = this.el.targets[i];
			if ($target.getAttribute('data-accordion-target') === id) {
				return $target;
			}
		}

		return null;
	}

	getTriggersById(id = '') {
		const triggers = [];
		for (let i = 0; i < this.el.triggers.length; i++) {
			const $trigger = this.el.triggers[i];
			if ($trigger.getAttribute('data-accordion-trigger') === id) {
				triggers.push($trigger);
			}
		}

		return triggers;
	}

	setExpandHeight($target, height = '') {
		if (this.config.disableDesktop && this.states.platform === Platform.DESKTOP) {
			$target.style.height = '';
		} else {
			$target.style.height = `${height}px`;
		}
	}

	collapse() {
		multiClassList(this.el.targets, 'remove', ['is-active', 'is-in']);
		multiClassList(this.el.triggers, 'remove', ['is-active', 'is-in']);

		for (let i = 0; i < this.el.targets.length; i++) {
			const $curTarget = this.el.targets[i];
			$curTarget.style.height = '';
		}

		this.states.index = -1;
	}

	// destroy

	destroy() {
		this.#removeEventListeners();
	}

}
